import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function MemberLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast.error("Email and password required");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/api/members/auth/login", { email: email.trim(), password }, { _silent401: true, _noOfflineQueue: true });
      const { token, user } = res.data;
      if (!token || !user) throw new Error("Invalid login response");
      
      // Store token
      localStorage.setItem("memberToken", token);
      localStorage.setItem("memberUser", JSON.stringify(user));
      
      toast.success(`Welcome back, ${user.name}! 🎉`);
      
      const requestedDestination = location.state?.from;
      const destination = requestedDestination?.pathname === "/members/free"
        ? requestedDestination
        : user?.tier === "free" ? "/members/free" : "/members-area";
      navigate(destination, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#0a0a0a" }}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6 relative">
            <div className="absolute inset-0 opacity-20 rounded-xl overflow-hidden" style={{ filter: "blur(12px)", transform: "scale(1.1)" }}>
              {/* Animated gradient background */}
              <div style={{
                background: "linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #3b82f6 100%)",
                width: "100%",
                height: "100%",
                animation: "gradient-shift 6s ease infinite",
              }} />
            </div>
            <h1 className="relative text-3xl font-black px-4 py-2">
              <span style={{ color: "#ec4899" }}>GP</span>
              <span style={{ color: "#fff" }}> Studio</span>
            </h1>
          </Link>
          <h2 className="text-2xl font-bold mb-2" style={{ color: "#fff" }}>
            Member Login
          </h2>
          <p className="text-sm" style={{ color: "#71717a" }}>
            Access your exclusive content
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-4 p-8 rounded-2xl border"
          style={{
            background: "rgba(24, 24, 27, 0.8)",
            backdropFilter: "blur(20px)",
            borderColor: "rgba(168, 85, 247, 0.2)",
          }}>
          
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
              Email
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
              <input
                id="member-email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm"
                style={{
                  background: "rgba(0, 0, 0, 0.3)",
                  borderColor: "rgba(113, 113, 122, 0.3)",
                  color: "#fff",
                }}
                disabled={loading}
                autoFocus
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: "#a1a1aa" }}>
              Password
            </label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#71717a" }} />
              <input
                id="member-password"
                autoComplete="current-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-12 py-3 rounded-lg border text-sm"
                style={{
                  background: "rgba(0, 0, 0, 0.3)",
                  borderColor: "rgba(113, 113, 122, 0.3)",
                  color: "#fff",
                }}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: "#71717a" }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div className="text-right">
            <Link to="/members/forgot-password" className="text-sm hover:underline" style={{ color: "#a855f7" }}>
              Forgot password?
            </Link>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition hover:scale-105 disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
              color: "#fff",
            }}>
            {loading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Logging in...
              </>
            ) : (
              <>
                Login
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        {/* Sign Up Link */}
        <div className="mt-6 text-center">
          <p className="text-sm" style={{ color: "#71717a" }}>
            Don't have an account?{" "}
            <Link to="/" className="font-semibold hover:underline" style={{ color: "#a855f7" }}>
              Sign up free
            </Link>
          </p>
        </div>

        {/* Back to Home */}
        <div className="mt-4 text-center">
          <Link to="/" className="text-sm hover:underline" style={{ color: "#71717a" }}>
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
